var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Table as AntTable, Tooltip, Dropdown, theme, Typography, Checkbox } from "antd";
import "./Table.scss";
import React, { useEffect, useState } from "react";
import { CaretDownOutlined, HolderOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import { CSS } from "@dnd-kit/utilities";
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { DndContext } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import Input from "../InputComponent/Input";
import { Button } from "../Button/Button";
const { useToken } = theme;
const Row = (_a) => {
    var { children } = _a, props = __rest(_a, ["children"]);
    const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
        id: props["data-row-key"]
    });
    const style = Object.assign(Object.assign(Object.assign({}, props.style), { transform: CSS.Transform.toString(transform && Object.assign(Object.assign({}, transform), { scaleY: 1 })), transition }), (isDragging ? { position: "relative" } : {}));
    // TODO: Drag and drop does not work with nested arrays but we don't seem to have this use case yet
    return (_jsx("tr", Object.assign({}, props, { ref: setNodeRef, style: style }, attributes, { children: React.Children.map(children, (child) => {
            if (child.key === "dragAndDrop") {
                return React.cloneElement(child, {
                    children: (_jsx(HolderOutlined, Object.assign({ ref: setActivatorNodeRef, style: { touchAction: "none", cursor: "move" } }, listeners)))
                });
            }
            return child;
        }) })));
};
const getContextRow = (RowComp) => (_a) => {
    var { menuprops } = _a, props = __rest(_a, ["menuprops"]);
    return (_jsx(Dropdown, { menu: menuprops, trigger: ["contextMenu"], children: _jsx(RowComp, Object.assign({}, props)) }));
};
/**
 *
 *
 * @description A table component that uses Ant Design's Table component.
 * @param data - Datasource for the table
 * @param size - Predefined table size
 * @param columns - Columns for the table
 * @param loading - If the table is loading
 * @param rowNumberHeader - If the row number header should be shown
 * @param statusHeader - Define a custom status header
 * @param pagination - Define pagination options
 * @param setLocalData - Callback to get value of local table data on certain table events such as on field change or on drag calls
 * @param onRow - Props per row. If this is used onRowClick is disabled.
 * @param onRowClick - What to do when a row is clicked
 * @param rowSelection - Props for row selection. If this is used onRowSelect is disabled.
 * @param onRowSelect - What to do when a row is selected
 * @param footer - Footer content
 * @param menuprops - Menu props
 * @param highlightIds - Which rows to highlight
 * @param virtual - If the table should be virtualized
 * @param scroll - Antd scroll options
 * @param showHeader - If the column headers should be shown
 * @param tableLayout - Layout type of the table
 * @param expandable - If the table should be expandable, and what should happen when it does. (If drag and drop, this should be undefined)
 * @param components - Override default table elements
 * @param emptyComponent - What component should be displayed if table data is empty
 * @param rowKey - The field which can be used as key
 * @param selectedAll - If the checkbox in selection column title is checked
 * @param onSelectedAllChange - The function which will be triggered when selectedAll changed
 * @param onChange - Will be called when chaging pagination, sorters or filters
 * @returns - A table component
 *
 * @deprecated Use component 'EditableTable' instead
 */
export const Table = ({ data, size = "small", columns, loading, rowNumberHeader = true, statusHeader, pagination, setLocalData, onRowClick, onRowSelect, headerExtra, footer, menuprops, highlightIds = [], virtual, scroll, showHeader, onRow, rowSelection, tableLayout, expandable, components, emptyComponent, rowKey = "id", selectedAll = false, onSelectedAllChange, onChange }) => {
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const { token } = useToken();
    useEffect(() => {
        setSelectedRowKeys([]);
        changeSelectedRowBasedOnSelectedAll(selectedAll);
    }, [data]);
    const getChildKeys = (data) => {
        let keys = [];
        if (data.children) {
            for (const child of data.children) {
                keys.push(child.id);
                keys = [...keys, ...getChildKeys(child)];
            }
        }
        return keys;
    };
    const changeSelectedRowBasedOnSelectedAll = (selected) => {
        let selectedRows = [];
        if (selected) {
            selectedRows = data.flatMap((item) => {
                return [item, ...(item.children || [])];
            });
        }
        setSelectedRowKeys(selectedRows.map((item) => item[rowKey]));
        if (onRowSelect) {
            onRowSelect(selectedRows);
        }
    };
    const onSelectAll = (selected) => {
        if (typeof onSelectedAllChange === "function") {
            onSelectedAllChange(selected);
        }
        changeSelectedRowBasedOnSelectedAll(selected);
    };
    const onSelectionChange = (selectedKeys, selectedRows) => {
        let allSelectedKeys = [...selectedKeys];
        const deselectedKeys = selectedRowKeys.filter((key) => !selectedKeys.includes(key)); // Determine the deselected row
        if (deselectedKeys.length > 0) {
            // Find the deselected row data
            const findRowData = (data, key) => {
                for (const item of data) {
                    if (item.id === key)
                        return item;
                    if (item.children) {
                        const childData = findRowData(item.children, key);
                        if (childData)
                            return childData;
                    }
                }
                return null;
            };
            const deselectedRowData = findRowData(data, deselectedKeys[0]);
            if (deselectedRowData) {
                const childKeys = getChildKeys(deselectedRowData);
                allSelectedKeys = allSelectedKeys.filter((key) => !childKeys.includes(key));
                // Remove descendants of the deselected row from selectedRows
                const toDeselect = new Set(childKeys);
                selectedRows = selectedRows.filter((row) => !toDeselect.has(row.id));
            }
            onSelectAll(false);
        }
        for (const row of selectedRows) {
            const childKeys = getChildKeys(row);
            allSelectedKeys = [...new Set([...allSelectedKeys, ...childKeys])]; // Set ensures uniqueness
        }
        const isParentSelected = (item, selectedList) => {
            for (const row of selectedList) {
                if (row.children) {
                    if (row.children.some((child) => child.id === item.id) ||
                        isParentSelected(item, row.children)) {
                        return true;
                    }
                }
            }
            return false;
        };
        const filteredSelectedRows = selectedRows.filter((row) => !isParentSelected(row, selectedRows));
        setSelectedRowKeys(allSelectedKeys);
        if (onRowSelect) {
            onRowSelect(filteredSelectedRows);
        }
    };
    const updateFieldValue = (data, recordId, value, dataIndex) => {
        return data.map((item) => {
            if (item.id === recordId) {
                return Object.assign(Object.assign({}, item), { [dataIndex]: value });
            }
            if (item.children) {
                return Object.assign(Object.assign({}, item), { children: updateFieldValue(item.children, recordId, value, dataIndex) });
            }
            return item;
        });
    };
    const onFieldValueChange = (value, record, dataIndex) => {
        const newData = updateFieldValue(data, record.id, value, dataIndex);
        if (setLocalData) {
            setLocalData(newData);
        }
    };
    /**
     * The logic to create header tooltips and have input elements be fields in the table was abstracted to simplify using the component.
     * We use those abstractions here and generate appropriate HTML structure.
     *
     * In case of a custom render function, we always allow the user to render their own content.
     */
    const enhancedColumns = columns.map((column) => (Object.assign(Object.assign({}, column), { align: column.key === "dragAndDrop" ? "right " : "left", title: (_jsxs(_Fragment, { children: [column.title, " ", column.tooltip && (_jsx(Tooltip, { title: column.tooltip, children: _jsx(QuestionCircleOutlined, {}) }))] })), render: (value, record, rowIdx) => {
            var _a;
            let cell = null;
            if (column.render) {
                cell = column.render(value, record, rowIdx);
            }
            else if ((_a = column.input) === null || _a === void 0 ? void 0 : _a.type) {
                if (column.input.type === "select") {
                    return (_jsx(Input.Select, Object.assign({}, column.input, { value: value, onClick: (e) => e.stopPropagation(), onChange: (value) => onFieldValueChange(value, record, column.dataIndex) })));
                }
                if (column.input.type === "checkbox") {
                    return (_jsx(Input.Checkbox, Object.assign({}, column.input, { checked: value, onClick: (e) => e.stopPropagation(), onChange: (e) => {
                            var _a;
                            onFieldValueChange((_a = e === null || e === void 0 ? void 0 : e.target) === null || _a === void 0 ? void 0 : _a.checked, record, column === null || column === void 0 ? void 0 : column.dataIndex);
                        } })));
                }
                return (_jsx(Input.Input, Object.assign({}, column.input, { value: value, onClick: (e) => e.stopPropagation(), onChange: (value) => onFieldValueChange(value, record, column.dataIndex) })));
            }
            else {
                cell = (_jsx(Tooltip, { title: value, mouseEnterDelay: 1, children: _jsx(Typography.Text, { ellipsis: true, children: value }) }));
            }
            // Drag drop not working with virtual table
            if (column.key === "dragAndDrop") {
                return null;
            }
            if (!virtual || !menuprops) {
                return cell;
            }
            // If we are using virtualization, we need to wrap context menu to the cell not the row
            return (_jsx(Dropdown, { menu: menuprops === null || menuprops === void 0 ? void 0 : menuprops(record), trigger: ["contextMenu"], children: _jsx("div", { style: { width: "100%", height: "100%" }, children: cell }) }));
        } })));
    const onDragEnd = ({ active, over }) => {
        if (active.id !== (over === null || over === void 0 ? void 0 : over.id)) {
            const activeIndex = data.findIndex((item) => item.id === active.id);
            const overIndex = data.findIndex((item) => item.id === (over === null || over === void 0 ? void 0 : over.id));
            if (activeIndex === -1 || overIndex === -1) {
                return; // Do nothing if indices are invalid.
            }
            const newData = arrayMove(data, activeIndex, overIndex);
            if (setLocalData) {
                setLocalData(newData);
            }
        }
    };
    return (_jsxs("div", { className: "table-wrapper", children: [(rowNumberHeader || statusHeader !== undefined) && (_jsxs("div", { className: "header", children: [_jsx(Typography.Text, { children: `Showing ${(pagination === null || pagination === void 0 ? void 0 : pagination.pageSize) || data.length} of ${(pagination === null || pagination === void 0 ? void 0 : pagination.total) || data.length} results` }), statusHeader !== undefined && statusHeader, _jsx("div", { className: "header-extra", children: headerExtra })] })), _jsx(DndContext, { modifiers: [restrictToVerticalAxis], onDragEnd: onDragEnd, children: _jsx(SortableContext, { items: data.map((i) => i.id), strategy: verticalListSortingStrategy, children: _jsx(AntTable, { size: size, rowKey: rowKey, components: components
                            ? components
                            : {
                                body: {
                                    row: menuprops ? getContextRow(Row) : Row
                                }
                            }, dataSource: data, locale: emptyComponent !== undefined
                            ? { emptyText: emptyComponent }
                            : undefined, columns: enhancedColumns, tableLayout: tableLayout, showHeader: showHeader, loading: loading, showSorterTooltip: false, pagination: pagination || false, rowSelection: rowSelection !== null && rowSelection !== void 0 ? rowSelection : (onRowSelect
                            ? {
                                type: "checkbox",
                                selectedRowKeys: selectedRowKeys, // use state to control selected row keys for visual representation
                                onChange: onSelectionChange,
                                onSelectAll,
                                columnWidth: 30,
                                columnTitle: typeof onSelectedAllChange === "function" ? (_jsx(Checkbox, { indeterminate: !selectedAll &&
                                        data.some((item) => selectedRowKeys.includes(item[rowKey])), checked: selectedAll, onChange: (e) => onSelectAll(e.target.checked) })) : undefined
                            }
                            : undefined), onRow: onRow !== null && onRow !== void 0 ? onRow : ((record) => ({
                            onClick: () => onRowClick === null || onRowClick === void 0 ? void 0 : onRowClick(record),
                            menuprops: menuprops === null || menuprops === void 0 ? void 0 : menuprops(record),
                            style: {
                                backgroundColor: highlightIds.includes(record.id)
                                    ? token.colorPrimaryBg
                                    : token.colorBgBase,
                                color: highlightIds.includes(record.id)
                                    ? token.colorPrimary
                                    : token.colorText
                            }
                        })), expandable: expandable !== null && expandable !== void 0 ? expandable : {
                            expandIcon: ({ expanded, onExpand, record }) => {
                                if (record.children && record.children.length > 0) {
                                    return (_jsx(Button, { className: "te-expand-icon", type: "text", onClick: (e) => {
                                            onExpand(record, e);
                                            e.stopPropagation();
                                        }, ghost: true, icon: _jsx(CaretDownOutlined, { style: {
                                                transform: `rotate(${expanded ? "0deg" : "270deg"})`
                                            } }) }));
                                }
                            }
                        }, footer: footer !== undefined ? () => footer : undefined, virtual: virtual, scroll: scroll, onChange: onChange }) }) })] }));
};
